import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { MentalHealthTreatmentHistoryModel } from '../Models/MentalHealthTreatmentHistoryModel/MentalHealthTreatmentHistoryModel.model';
import { GridRowId } from '@mui/x-data-grid';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';

const mentalHealthTreatmentHistorySlice = createSlice({
    name: 'mentalHealthTreatmentHistorySlice',
    initialState: {
        mentalHealthTreatmentHistoryInfo: new MentalHealthTreatmentHistoryModel(),
        isFetchingMentalHealthTreatmentHistory: false,
        selectedTherapistIds: [],
    },
    reducers: {
        _setIsFetchingMentalHealthTreatmentHistory(state, action) {
            state.isFetchingMentalHealthTreatmentHistory = action.payload;
        },
        _setMentalHealthTreatmentHistoryInfo(state, action) {
            state.mentalHealthTreatmentHistoryInfo = action.payload;
        },
        _setSelectedTherapistIds(state, action) {
            state.selectedTherapistIds = action.payload;
        },
    },
});

const { _setIsFetchingMentalHealthTreatmentHistory, _setMentalHealthTreatmentHistoryInfo, _setSelectedTherapistIds } =
    mentalHealthTreatmentHistorySlice.actions;

export const setMentalHealthTreatmentHistoryInfo =
    (mentalHealthTreatmentHistoryInfo: MentalHealthTreatmentHistoryModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setMentalHealthTreatmentHistoryInfo(mentalHealthTreatmentHistoryInfo));
    };

export const setIsFetchingMentalHealthTreatmentHistory =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingMentalHealthTreatmentHistory(isFetching));
    };

export const setSelectedTherapistIds =
    (selectedTherapistIds: GridRowId[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setSelectedTherapistIds(selectedTherapistIds));
    };

export const saveConfirmBHTherapists =
    (memberId: string, selectedTherapists: any[]): AppThunk =>
    async (dispatch, getStatue) => {
        await axios.put(`${API_URL}/member/confirmbhtherapist?memberId=${memberId}`, selectedTherapists);
    };

export const saveMentalHealthTreatmentHistoryInfo =
    (mentalHealthTreatmentHistoryInfo: MentalHealthTreatmentHistoryModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(mentalHealthTreatmentHistoryInfo.Id);
        const url = isUpdate
            ? `${API_URL}/mentalhealthtreatmenthistory/updatementalhealthtreatmenthistory/`
            : `${API_URL}/mentalhealthtreatmenthistory/insertmentalhealthtreatmenthistory/`;

        try {
            const response = await axios.put(url, mentalHealthTreatmentHistoryInfo);
            const savedMentalHealthTreatmentHistory = isUpdate ? mentalHealthTreatmentHistoryInfo : response.data;
            if (Boolean(response.data)) {
                dispatch(
                    setMentalHealthTreatmentHistoryInfo(
                        isUpdate ? mentalHealthTreatmentHistoryInfo : { ...mentalHealthTreatmentHistoryInfo, Id: response.data.Id }
                    )
                );
            }
            const isDraftSaved = await dispatch(prepareMentalHealthTreatmentHistoryNotes(savedMentalHealthTreatmentHistory));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareMentalHealthTreatmentHistoryNotes =
    (mentalHealthTreatmentHistoryInfo: MentalHealthTreatmentHistoryModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(mentalHealthTreatmentHistoryInfo.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory);
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.MentalHealthTreatmentHistory);
            summaryNotes.push({
                Id: mentalHealthTreatmentHistoryInfo.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Mental Health Treatment History Assessment Completed',
                Type: SummaryNoteTypeEnum.MentalHealthTreatmentHistory,
                IsConfirm: true,
                DisplayName: 'Mental Health Treatment History',
            });
            const updatedEngagementDraft = {
                ...engagementDraft,
                SummaryNotes: summaryNotes,
                MentalHealthTreatmentHistoryId: mentalHealthTreatmentHistoryInfo.Id,
            };
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                MentalHealthTreatmentHistoryStatus: mentalHealthTreatmentHistoryInfo.AssessmentStatus,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchMentalHealthTreatmentHistoryInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingMentalHealthTreatmentHistory(true));
        const response = await axios.get(`${API_URL}/mentalhealthtreatmenthistory/fetchmentalhealthtreatmenthistoryinfobyid?id=${id}`);
        if (!response.data) {
            dispatch(setMentalHealthTreatmentHistoryInfo(new MentalHealthTreatmentHistoryModel()));
            dispatch(setIsFetchingMentalHealthTreatmentHistory(false));
            return;
        }
        dispatch(setMentalHealthTreatmentHistoryInfo(response.data));
        dispatch(setIsFetchingMentalHealthTreatmentHistory(false));
    };

export const fetchMentalHealthTreatmentHistoryInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingMentalHealthTreatmentHistory(true));
        const response = await axios.get(`${API_URL}/mentalhealthtreatmenthistory/fetchmentalhealthtreatmenthistoryinfobymemberid?memberId=${memberId}`);
        if (!response.data) {
            dispatch(setMentalHealthTreatmentHistoryInfo(new MentalHealthTreatmentHistoryModel()));
            dispatch(setIsFetchingMentalHealthTreatmentHistory(false));
            return;
        }
        preparePriorAssessmentForNewDraft(response.data);
        if (Array.isArray(response.data.MemberHospitalizations)) {
            response.data.MemberHospitalizations.forEach((hospitalization: any) => {
                if (hospitalization) {
                    hospitalization.Id = null;
                }
            });
        }
        dispatch(setMentalHealthTreatmentHistoryInfo(response.data));
        dispatch(setIsFetchingMentalHealthTreatmentHistory(false));
    };

export default mentalHealthTreatmentHistorySlice.reducer;
